/**
 * Motion system — single source of truth for durations, easings,
 * keyframes, the reduced-motion hook and the standard loading
 * indicators (motion-polish plan §2.1 / §2.3).
 *
 * Principles (plan §1):
 *   1. Motion never moves layout — only `opacity` / `transform`
 *      (compositor-only). Real height changes go through the
 *      `HeightTransition` primitive in dashboard-render.
 *   2. Tokens are defined ONCE here and consumed by both apps and by
 *      the dashboard-render primitives. Values are estimates — they
 *      are calibrated visually in the "Transitions" gallery.
 *   3. `prefers-reduced-motion: reduce` disables everything
 *      decorative; state feedback (a static spinner arc, the solid
 *      progress hairline) stays visible without animating.
 *
 * The loading language has 3 levels (plan §2.3):
 *   - Global  → `GlobalProgressBar` (2px magenta hairline, top)
 *   - Card    → `RefreshShimmer` (used inside dashboard-render's
 *               `RefreshOverlay` wrapper: dim + shimmer)
 *   - Action  → `Spinner` (16/20px, stroke 2, accent, aria-label)
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { brand } from '../brand.js';

// ── Tokens ──────────────────────────────────────────────────
//
// Durations in ms, easings as CSS cubic-bezier strings. These are the
// ONLY motion constants — never hardcode a duration/easing in a
// component; import `MOTION`. Values marked calibratable: tune them
// while watching the Transitions gallery (plan §5 risk #2).
export const MOTION = {
  /** Micro feedback: hovers, chip toggles, small fades. */
  durFast: 120,
  /** Default for content transitions: crossfades, number tweens. */
  durBase: 180,
  /** Larger structural moves: height transitions, panel reveals. */
  durSlow: 280,
  /** One-time entrance grows ("de cero al valor", ronda 12). Longer
   *  than durSlow on purpose — at 280ms the grow read as a blink,
   *  and at 700ms still too quick (user feedback 2026-07-23 ×2:
   *  "es muy rapido" → "incluso un poco mas lenta"). Decorative
   *  mount-once motion, so the >300ms primary-content rule does not
   *  apply: the final value is what matters. */
  durEntrance: 1000,
  /** Pause between the element being painted at zero and the entrance
   *  starting. Without it, entrances kicked off while the dashboard is
   *  still laying out drop their first frames and the animation seems
   *  to "start from the middle" (user feedback 2026-07-23 on line
   *  charts). The settle beat also reads more deliberate. */
  delayEntrance: 250,
  /** Decelerating — entrances and content arriving. */
  easeOut: 'cubic-bezier(.2,.8,.2,1)',
  /** Symmetric — reversible moves (height, position). */
  easeInOut: 'cubic-bezier(.4,0,.2,1)',
  /** Cambio de VISTA dentro de una misma celda (tabla <-> detalle de
   *  fila). Entre `durBase` (180) y `durSlow` (280): a 180 el
   *  desplazamiento lateral se lee como un salto y deja de comunicar
   *  la direccion, que es justamente para lo que existe; a 280 se
   *  siente lento en algo que el usuario repite fila tras fila.
   *  Elegido con Pablo, 2026-09-09. */
  durView: 220,
};

// ── Keyframes / global styles (registered once) ─────────────
//
// Same idea as `pmRowDrillIn` (RowDrillPanel, PR #54) but registered
// a single time in <head> instead of per-instance <style> tags, since
// several independent components share these keyframes.
const MOTION_STYLE_ID = 'pm-motion-styles';

const MOTION_CSS = `
@keyframes pmSpin { to { transform: rotate(360deg); } }
@keyframes pmFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes pmRiseIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: none; }
}
@keyframes pmProgressSlide {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}
@keyframes pmShimmerSweep {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
@keyframes pmSparkle {
  0%, 100% { transform: none; opacity: 1; }
  30%      { transform: rotate(-14deg) scale(1.3); opacity: .85; }
  60%      { transform: rotate(10deg) scale(1.12); }
}
/* Cambio de VISTA (tabla -> detalle de fila y vuelta). Las dos caras
   recorren el 100% del ancho a la misma velocidad, asi que en cada
   frame se tocan borde a borde y el fondo de la tarjeta no aparece
   nunca por el medio. "Volver" corre los mismos dos keyframes con la
   direccion invertida: el usuario reconoce el gesto y la direccion le
   dice si avanzo o retrocedio. */
@keyframes pmViewOutLeft {
  from { transform: none; }
  to   { transform: translateX(-100%); }
}
@keyframes pmViewInRight {
  from { transform: translateX(100%); }
  to   { transform: none; }
}
@keyframes pmViewOutRight {
  from { transform: none; }
  to   { transform: translateX(100%); }
}
@keyframes pmViewInLeft {
  from { transform: translateX(-100%); }
  to   { transform: none; }
}
`;

/**
 * Idempotently injects the shared motion keyframes into
 * `document.head`. Safe to call from every component render path
 * (no-ops after the first call; no-ops entirely outside the browser).
 */
export function ensureMotionStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(MOTION_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = MOTION_STYLE_ID;
  el.textContent = MOTION_CSS;
  document.head.appendChild(el);
}

// ── Reduced motion ──────────────────────────────────────────

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Context override for `usePrefersReducedMotion` — lets a subtree
 * force reduced motion ON/OFF regardless of the OS setting. Used by
 * the Transitions gallery toggle; apps normally never mount it (the
 * OS media query rules).
 */
const MotionConfigContext = createContext(null);

/**
 * `<MotionConfig reducedMotion={boolean}>` — subtree override for the
 * reduced-motion preference. `null`/absent = follow the OS setting.
 */
export function MotionConfig({ reducedMotion = null, children }) {
  return (
    <MotionConfigContext.Provider value={reducedMotion}>
      {children}
    </MotionConfigContext.Provider>
  );
}

/**
 * True when animations should be skipped. Resolution order:
 *   1. Nearest `<MotionConfig reducedMotion={…}>` override (gallery).
 *   2. Live `prefers-reduced-motion: reduce` media query (listener
 *      attached/cleaned like `useIsDesktop` in DashboardBody).
 *   3. `false` when `matchMedia` is unavailable (jsdom without mock).
 */
export function usePrefersReducedMotion() {
  const override = useContext(MotionConfigContext);
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(REDUCED_MOTION_QUERY).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (e) => setReduced(e.matches);
    mql.addEventListener('change', onChange);
    setReduced(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return override !== null && override !== undefined ? override : reduced;
}

// ── Entrance suppression (FadeSwap ghosts) ──────────────────
//
// FadeSwap's outgoing face renders the PREVIOUS subtree as a fresh
// mount — without suppression, every fromZero/entrance hook inside
// the ghost restarted at ZERO counting toward the OLD data, painted
// ON TOP of the live card ("se resetean a cero y parte del valor pre
// filtro", 2026-07-23). Ghosts are snapshots: they must render their
// values settled, never animate an entrance.
const EntranceSuppressContext = createContext(false);

/** Wrap a subtree whose entrance animations must NOT run (FadeSwap
 *  outgoing snapshots). Everything inside mounts settled. */
export function SuppressEntrance({ children }) {
  return (
    <EntranceSuppressContext.Provider value={true}>
      {children}
    </EntranceSuppressContext.Provider>
  );
}

/** True inside a `<SuppressEntrance>` subtree. Consumed by
 *  useEntrance / useCountUp here and NumberTween in dashboard-render. */
export function useEntranceSuppressed() {
  return useContext(EntranceSuppressContext);
}

// ── Entrance (grow from zero) ───────────────────────────────

/**
 * `useEntrance()` — mount-once entrance flag for "grow from zero to
 * value" animations (ronda 12: meters, share bars, rings, columns).
 *
 * Returns `false` on the FIRST paint and flips to `true` on the next
 * frame, so a CSS transition carries the element from its zero state
 * to its real value. Under reduced motion it is `true` immediately —
 * the element mounts at its final value, no theatrics.
 *
 * Mount-once by design: data refrees update the already-entered
 * element in place (the value transition handles the morph); the
 * zero-grow plays only when the component instance first appears.
 * Motion stays compositor-only at the call sites (transform /
 * stroke-dashoffset / opacity) per the motion rules.
 */
export function useEntrance() {
  const reduced = usePrefersReducedMotion();
  const suppressed = useEntranceSuppressed();
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (suppressed) {
      setEntered(true);
      return undefined;
    }
    if (reduced) {
      setEntered(true);
      return undefined;
    }
    if (typeof requestAnimationFrame !== 'function') {
      setEntered(true);
      return undefined;
    }
    // Hidden/occluded documents: Chrome pauses rAF entirely, so the
    // double-rAF below would never fire and every entrance element
    // would sit frozen at ZERO (a lying value on a BI surface). No one
    // is watching a hidden tab — enter immediately, no animation.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      setEntered(true);
      return undefined;
    }
    // Start sequence (ronda 14 fix): double rAF guarantees the zero
    // state actually PAINTED (a single rAF can coalesce with the first
    // paint and skip the animation), then requestIdleCallback waits for
    // the main thread to go quiet — SVG transitions (stroke-dashoffset,
    // transforms inside <svg>) paint on the MAIN thread, so an entrance
    // started while the dashboard grid is still laying out drops its
    // first frames and the line "starts from the middle". Only THEN the
    // deliberate `delayEntrance` beat runs, and the animation plays on
    // a quiet thread from a real zero.
    let raf2 = null;
    let idleId = null;
    let delay = null;
    const idleThen = () => {
      delay = setTimeout(() => setEntered(true), MOTION.delayEntrance);
    };
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (typeof requestIdleCallback === 'function') {
          // timeout caps the wait — a busy page still animates soon.
          idleId = requestIdleCallback(idleThen, { timeout: 600 });
        } else {
          idleThen();
        }
      });
    });
    // Backstop: an OCCLUDED-but-not-hidden window (covered by another
    // window) also pauses rAF while visibilityState stays "visible".
    // A throttled timeout still fires eventually and flips the flag —
    // the animation is lost, the correct final value is not. Sized to
    // the full start sequence (idle timeout + delay) plus margin.
    const backstop = setTimeout(() => setEntered(true), MOTION.delayEntrance + 1050);
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      if (idleId !== null && typeof cancelIdleCallback === 'function') cancelIdleCallback(idleId);
      if (delay !== null) clearTimeout(delay);
      clearTimeout(backstop);
    };
  }, [reduced, suppressed]);
  return reduced || suppressed ? true : entered;
}

/**
 * `useCountUp(target, opts)` — numeric count-up for value labels that
 * live INSIDE bi-components (MeterBar %, tile counts) where
 * dashboard-render's NumberTween can't reach (dep graph points the
 * other way). Returns the number to format each frame; the caller's
 * own deterministic formatter guarantees the landed frame renders
 * byte-identical to the static path.
 *
 * Same non-negotiable guards as NumberTween (regla BI — a frozen
 * intermediate number is a lie): reduced motion / hidden document →
 * target immediately; hiding mid-count snaps; a timeout backstop
 * covers occluded-but-"visible" windows where rAF pauses silently.
 * Start beat mirrors the CSS entrances: `MOTION.delayEntrance` hold,
 * then `MOTION.durEntrance` ease-out from zero. Non-finite targets
 * return the target untouched (caller renders its own em-dash).
 */
export function useCountUp(target, { disabled = false } = {}) {
  const reduced = usePrefersReducedMotion();
  const suppressed = useEntranceSuppressed();
  const numeric = typeof target === 'number' && Number.isFinite(target);
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(display);
  displayRef.current = display;
  // Entrance bookkeeping (3 refs, audit 2026-07-24 — StrictMode
  // idempotence). `enteredRef`: an entrance was already STARTED for
  // this instance. `entranceTargetRef`: the target it started for.
  // `entrancePaintedRef`: that entrance actually committed a value.
  // React's dev double-invoke replays mount effects (setup → cleanup →
  // setup) with IDENTICAL deps; marking `entered` in the effect body
  // alone turned the second run into the 180ms MORPH branch, so every
  // dev entrance was a blink instead of the 1s count. A replay is
  // identified as "same target, nothing painted yet" and re-runs the
  // ENTRANCE. Production behaviour is untouched: a real target change
  // is never a replay (different target), and a change after the first
  // frame is never a replay (painted).
  const enteredRef = useRef(false);
  const entranceTargetRef = useRef(undefined);
  const entrancePaintedRef = useRef(false);
  useEffect(() => {
    if (!numeric || suppressed) return undefined;
    const replay =
      enteredRef.current &&
      !entrancePaintedRef.current &&
      Object.is(entranceTargetRef.current, target);
    // Entrance is mount-once. LATER target changes (filter refetches)
    // MORPH from the currently painted value to the new one over
    // durBase — a dry snap beside a smoothly-morphing bar read as
    // "parpadeo" (user feedback 2026-07-23). Same guards as the
    // entrance count: reduced/hidden/no-rAF → instant.
    if (enteredRef.current && !replay) {
      // Any morph commits a value, so no LATER run can be a replay —
      // not even one whose target happens to equal the entrance target.
      entrancePaintedRef.current = true;
      const from = displayRef.current;
      if (
        disabled ||
        reduced ||
        from === target ||
        typeof requestAnimationFrame !== 'function' ||
        (typeof document !== 'undefined' && document.visibilityState === 'hidden')
      ) {
        setDisplay(target);
        return undefined;
      }
      let raf = null;
      let start = null;
      const step = (ts) => {
        if (start === null) start = ts;
        const t = Math.min((ts - start) / MOTION.durBase, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(t >= 1 ? target : from + (target - from) * eased);
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') {
          if (raf !== null) cancelAnimationFrame(raf);
          setDisplay(target);
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      const backstop = setTimeout(() => {
        if (raf !== null) cancelAnimationFrame(raf);
        setDisplay(target);
      }, MOTION.durBase + 300);
      return () => {
        if (raf !== null) cancelAnimationFrame(raf);
        clearTimeout(backstop);
        document.removeEventListener('visibilitychange', onVisibility);
      };
    }
    enteredRef.current = true;
    entranceTargetRef.current = target;
    entrancePaintedRef.current = false;
    // Every path that commits a value marks the entrance as painted —
    // from here on a re-run is a real change (morph), never a replay.
    const land = () => {
      entrancePaintedRef.current = true;
      setDisplay(target);
    };
    if (
      disabled ||
      reduced ||
      typeof requestAnimationFrame !== 'function' ||
      (typeof document !== 'undefined' && document.visibilityState === 'hidden')
    ) {
      land();
      return undefined;
    }
    let raf = null;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const t = Math.min((ts - start) / MOTION.durEntrance, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      entrancePaintedRef.current = true;
      setDisplay(t >= 1 ? target : target * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    const delay = setTimeout(() => {
      raf = requestAnimationFrame(step);
    }, MOTION.delayEntrance);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (raf !== null) cancelAnimationFrame(raf);
        land();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const backstop = setTimeout(() => {
      if (raf !== null) cancelAnimationFrame(raf);
      land();
    }, MOTION.delayEntrance + MOTION.durEntrance + 500);
    return () => {
      clearTimeout(delay);
      clearTimeout(backstop);
      if (raf !== null) cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [numeric, disabled, reduced, target]);
  if (!numeric) return target;
  if (disabled || reduced || suppressed) return target;
  return display;
}

/** Zero (pre-entrance) transform per mode. `fade` does not scale —
 *  scaling density marks lies about their position. */
const ENTRANCE_ZERO = {
  growY: 'scaleY(0)',
  growX: 'scaleX(0)',
  pop: 'scale(0)',
  fade: 'none',
};

const ENTRANCE_TRANSITION = `transform ${MOTION.durEntrance}ms ${MOTION.easeOut}, opacity ${MOTION.durEntrance}ms ${MOTION.easeOut}`;

/**
 * Controlled mode — renders PURELY from the `drawn` prop. **Zero
 * hooks by design** (audit 2026-07-24): since r16 the chart owns the
 * cycle through `useGrowCycle`, but this group still called
 * `useEntrance()` + `usePrefersReducedMotion()` once PER MARK — a
 * 52-bar chart paid ~212 `matchMedia` reads and ~106 live
 * subscriptions whose output was then discarded. Nothing here needs
 * the reduced-motion query either: `useGrowCycle` returns `true` from
 * its very first render under reduced motion, so the group mounts at
 * its final state and the declared transition has nothing to animate.
 *
 * While `drawn` is false the zero state renders WITHOUT transition (a
 * transition there would animate backwards on the reset frame).
 */
function EntranceGroupControlled({ children, mode, origin, disabled, drawn }) {
  // ONE context read — no state, no effect, no MediaQueryList
  // subscription (that is the whole point of the controlled split).
  // It exists for a real hole found in review (2026-07-24): the OWNER
  // resolves reduced-motion once via `useGrowCycle`, but if the
  // preference flips to "reduce" WHILE this group sits in its zero
  // window, `drawn` goes false→true and the still-declared transition
  // animates for a full second right after the user asked for less
  // motion. In the apps the CSS blanket net masks it; the gallery
  // toggle drives `MotionConfig` (not the media query), so nothing
  // else covers it there — and the gallery is the UI/UX source of
  // truth (regla 13): a card that animates on "reduced motion" is
  // exactly the demo that lies.
  const override = useContext(MotionConfigContext);
  if (disabled) return <g>{children}</g>;
  return (
    <g
      style={{
        transform: drawn ? 'none' : ENTRANCE_ZERO[mode],
        transformOrigin: origin,
        opacity: mode === 'fade' && !drawn ? 0 : 1,
        transition: drawn && override !== true ? ENTRANCE_TRANSITION : undefined,
      }}
    >
      {children}
    </g>
  );
}

/**
 * Autonomous mode (no `drawn` prop) — the group runs its OWN
 * mount-once entrance via `useEntrance()`: it enters on mount and
 * never re-animates, because nothing tells it the data changed.
 *
 * As of the 2026-07-24 audit sweep NO call site in this package uses
 * it — analytics and maps were migrated to the chart-owned cycle in
 * the same sweep, so every widget re-animates together when a filter
 * lands. It stays as the documented default of the public
 * `EntranceGroup` API (a consumer wrapping its own marks gets a sane
 * mount entrance without wiring `useGrowCycle`), and it is the branch
 * a widget falls back to if someone forgets `drawn`. Prefer the
 * controlled mode for anything data-driven. Hooks run unconditionally,
 * before the `disabled` early return.
 */
function EntranceGroupAutonomous({ children, mode, origin, disabled }) {
  const entered = useEntrance();
  const reduced = usePrefersReducedMotion();
  if (disabled) return <g>{children}</g>;
  return (
    <g
      style={{
        transform: entered ? 'none' : ENTRANCE_ZERO[mode],
        transformOrigin: origin,
        opacity: mode === 'fade' && !entered ? 0 : 1,
        transition: reduced ? undefined : ENTRANCE_TRANSITION,
      }}
    >
      {children}
    </g>
  );
}

/**
 * `EntranceGroup` — the standard SVG wrapper that gives a chart's DATA
 * MARKS their mount entrance ("de cero al valor", ronda 12 sweep:
 * every widget animates by default, opt-out via the chart's
 * `entrance={false}` prop).
 *
 * Wrap ONLY the marks (bars, slices, dots, cells) — never axes or
 * text, which would visibly distort while the group scales.
 *
 * Modes (all compositor-only — transform/opacity):
 *   - 'growY'  bars growing up from the baseline. Pass `origin` as
 *              the baseline point, e.g. `0px ${baselineY}px`.
 *   - 'growX'  horizontal bars growing from the left. `origin` left
 *              edge, e.g. `${x0}px 0px`.
 *   - 'pop'    radial marks (pie, radar, treemap) scaling from their
 *              center. `origin` = `${cx}px ${cy}px`.
 *   - 'fade'   density marks where scaling lies about position
 *              (scatter, bubbles, heat cells, maps).
 *
 * Line-family charts do NOT use this wrapper — they draw in via
 * `pathLength={1}` + `useLineDraw()` (stroke-dashoffset), which keeps
 * dots and labels undistorted.
 *
 * Two modes, dispatched on `drawn` (never a conditional hook):
 *   - `drawn` PASSED → controlled, hook-free render driven by the
 *     chart's `useGrowCycle` cycle. EVERY call site in this package
 *     is here: charts since r16, analytics + maps since the audit
 *     sweep of 2026-07-24.
 *   - `drawn` OMITTED → autonomous, own `useEntrance()` (mount-once,
 *     never re-animates). No in-package caller; it is the default of
 *     the public API for external consumers.
 * A call site must be consistent — always pass `drawn` or never —
 * because switching branches swaps the rendered component type and
 * would remount the marks. Passing `undefined` for a boolean you
 * *meant* to compute silently downgrades that group to mount-once.
 *
 * `disabled` renders a plain `<g>` (the per-chart `entrance` opt-out;
 * reduced motion lands settled through `useGrowCycle`/`useEntrance`).
 */
export function EntranceGroup({ children, mode = 'fade', origin, disabled = false, drawn }) {
  return drawn === undefined ? (
    <EntranceGroupAutonomous mode={mode} origin={origin} disabled={disabled}>
      {children}
    </EntranceGroupAutonomous>
  ) : (
    <EntranceGroupControlled mode={mode} origin={origin} disabled={disabled} drawn={drawn}>
      {children}
    </EntranceGroupControlled>
  );
}

/** How long a redraw reset may last before it is force-released. Two
 *  painted frames (~33ms) is the real duration; the rest is margin for
 *  a throttled/occluded window. */
const REDRAW_BACKSTOP_MS = 450;

/**
 * `useRedrawPulse(redrawKey, inert)` — the shared "reset for one
 * painted frame, then release" machinery behind `useGrowCycle` (bars
 * re-grow) and `useLineDraw` (stroke redraws). Returns `redrawing`.
 *
 * Lives here ONCE on purpose: both consumers reset their marks to a
 * zero/undrawn state, and a reset that never clears is the regla BI
 * failure mode — bars flat at zero, an invisible line, PERMANENTLY.
 * Two construction-level guarantees make that unreachable:
 *
 *   1. The release backstop is armed OUTSIDE the pulse effect (its own
 *      ref + unmount-only cleanup). The audit found a path where a
 *      second data change landing inside the ~33ms reset window, while
 *      the guards tripped (tab hidden), cancelled the effect that owned
 *      the ONLY timer able to clear the reset. A backstop the pulse
 *      effect cannot cancel removes that path entirely.
 *   2. The guarded branch (settled paint: reduced motion, FadeSwap
 *      ghost, hidden document, no rAF) explicitly RELEASES the reset
 *      instead of returning early with it still latched.
 *
 * Both transitions only ever move `redrawing` toward false, so the
 * worst case is a redraw that ends sooner — never data stuck at zero.
 */
function useRedrawPulse(redrawKey, inert) {
  const [redrawing, setRedrawing] = useState(false);
  const keyRef = useRef(redrawKey);
  const backstopRef = useRef(null);
  useEffect(
    () => () => {
      if (backstopRef.current !== null) clearTimeout(backstopRef.current);
    },
    [],
  );
  useEffect(() => {
    if (redrawKey === undefined) return undefined;
    if (Object.is(keyRef.current, redrawKey)) return undefined;
    keyRef.current = redrawKey;
    if (
      inert ||
      typeof requestAnimationFrame !== 'function' ||
      (typeof document !== 'undefined' && document.visibilityState === 'hidden')
    ) {
      setRedrawing(false); // new data paints settled, no theatrics
      return undefined;
    }
    setRedrawing(true);
    // Double rAF: the zero (undrawn) state must PAINT before the
    // transition target applies, or the redraw skips.
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setRedrawing(false));
    });
    if (backstopRef.current !== null) clearTimeout(backstopRef.current);
    backstopRef.current = setTimeout(() => {
      backstopRef.current = null;
      setRedrawing(false);
    }, REDRAW_BACKSTOP_MS);
    // NOTE: the backstop is deliberately NOT cleared here — see the
    // hook's docstring. Cancelling it is exactly how the reset strands.
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
    };
  }, [redrawKey, inert]);
  return redrawing;
}

/**
 * `useGrowCycle(redrawKey, disabled)` — the chart-level grow driver
 * for `EntranceGroup`'s controlled mode. Returns `drawn`:
 *   - first mount: false → (entrance sequence: paint zero → idle →
 *     `delayEntrance` beat) → true — the normal entrance.
 *   - `redrawKey` change (a `lineDataSig` of the plotted values):
 *     false for one painted frame (no transition) → true — ALL marks
 *     re-grow together, the bar-family analog of the line redraw.
 * Guards mirror the rest of the system: reduced motion, FadeSwap
 * ghosts and hidden documents render settled; a timeout backstop
 * covers occluded windows. Same data ⇒ same key ⇒ no replay.
 */
export function useGrowCycle(redrawKey, disabled = false) {
  const entered = useEntrance();
  const reduced = usePrefersReducedMotion();
  const suppressed = useEntranceSuppressed();
  const inert = disabled || reduced || suppressed;
  const redrawing = useRedrawPulse(redrawKey, inert);
  if (inert) return true;
  return entered && !redrawing;
}

/**
 * `useLineDraw(disabled)` — entrance for the line family: the stroke
 * draws in left-to-right via `pathLength`-normalized dash offset (no
 * geometric scaling, so dots/labels stay crisp). Returns props to
 * spread on the `<path>`/`<polyline>`:
 *
 *   <path {...lineProps} d={d} pathLength={1} />
 *
 * Companion marks (dots, value labels) should fade with
 * `useEntrance()` opacity so they land with the stroke.
 */
export function useLineDraw(disabled = false, redrawKey = undefined) {
  const entered = useEntrance();
  const reduced = usePrefersReducedMotion();
  const suppressed = useEntranceSuppressed();
  const inert = disabled || reduced || suppressed;
  // Redraw on data change (decisión 2026-07-23): unlike numbers and
  // bars, a line has no honest "morph" to its new shape — the path
  // hard-swaps. When `redrawKey` (a signature of the plotted values)
  // changes after the entrance, the stroke resets WITHOUT transition
  // and draws in again left-to-right. What it progressively reveals
  // IS the new data — no invented intermediates. `useRedrawPulse`
  // owns the reset + its unstrandable release.
  const redrawing = useRedrawPulse(redrawKey, inert);
  if (inert) return {};
  return {
    pathLength: 1,
    strokeDasharray: 1,
    strokeDashoffset: entered && !redrawing ? 0 : 1,
    // No transition while resetting to zero — otherwise the stroke
    // would animate BACKWARDS before redrawing.
    style: redrawing
      ? undefined
      : { transition: `stroke-dashoffset ${MOTION.durEntrance}ms ${MOTION.easeOut}` },
  };
}

/**
 * `lineDataSig(values)` — cheap djb2 signature of a series' plotted
 * values, for `useLineDraw`'s `redrawKey`. Same data ⇒ same key ⇒ no
 * redraw (first-page cache returns identical payloads); any value
 * change ⇒ redraw.
 */
export function lineDataSig(values) {
  const json = JSON.stringify(values);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = ((h << 5) + h + json.charCodeAt(i)) | 0;
  }
  return h;
}

// ── Level 3 — action spinner ────────────────────────────────

/**
 * Standard inline spinner (loading level 3 — a user action in
 * flight: export button, search, load-more). 16px default / 20px for
 * standalone placement, stroke 2, accent magenta — sized and colored
 * so it IS noticeable over both the light card background and the
 * dark banner (the "spinners no se notan" fix).
 *
 * `ariaLabel` is mandatory in spirit: it defaults to "Loading" but
 * every call site should pass the real action ("Exporting…").
 * Reduced motion: renders the static arc (state feedback stays, the
 * rotation goes).
 */
export function Spinner({
  size = 16,
  ariaLabel = 'Loading',
  color = brand.accent.DEFAULT,
  className = '',
}) {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();
  const r = (size - 2) / 2; // stroke 2 → keep the circle inside the box
  const c = 2 * Math.PI * r;
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      // Stable hook for "is anything still loading?" DOM queries — the
      // server-side dashboard capture (F7-R2 #1) refuses to shoot while
      // one of these is inside the captured node. Keep the attribute.
      data-pm-spinner=""
      className={`inline-flex ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        aria-hidden="true"
        style={
          reduced
            ? undefined
            : { animation: `pmSpin 800ms linear infinite` }
        }
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeOpacity="0.25"
          strokeWidth="2"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${c * 0.28} ${c * 0.72}`}
        />
      </svg>
    </span>
  );
}

// ── Level 1 — global progress hairline ──────────────────────

/**
 * Indeterminate 2px hairline progress bar (loading level 1 — "some
 * refetch is in flight, portal-wide"). Designed for the HOST to mount
 * fixed at the top of its chrome; `fixed` positioning is the default
 * but can be turned off (`fixed={false}`) to embed it at the top of a
 * scoped container (builder Preview modal — plan §5 #4).
 *
 * Renders nothing when `active` is false. Reduced motion: a static
 * solid hairline (presence = the signal) instead of the sliding
 * segment.
 */
export function GlobalProgressBar({
  active = true,
  fixed = true,
  ariaLabel = 'Loading',
  className = '',
}) {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();
  if (!active) return null;
  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      data-pm-global-progress
      className={`${fixed ? 'fixed top-0 left-0 right-0' : 'relative w-full'} z-50 overflow-hidden ${className}`}
      style={{ height: 2, background: `color-mix(in srgb, ${brand.accent.DEFAULT} 15%, transparent)` }}
    >
      {reduced ? (
        <div
          className="h-full w-full"
          style={{ background: brand.accent.DEFAULT }}
        />
      ) : (
        <div
          className="h-full w-1/4"
          style={{
            background: brand.accent.DEFAULT,
            animation: `pmProgressSlide 1200ms ${MOTION.easeInOut} infinite`,
          }}
        />
      )}
    </div>
  );
}

// ── Level 2 — card refresh shimmer ──────────────────────────

/**
 * Subtle diagonal light sweep, absolutely positioned over the card it
 * decorates (the parent supplies `position: relative`). This is the
 * CSS base of loading level 2; the stateful wrapper (`RefreshOverlay`
 * — dim + delay + pointer handling) lives in dashboard-render because
 * it is composition-level. Reduced motion: renders nothing (the dim
 * that `RefreshOverlay` applies is the remaining state feedback).
 */
export function RefreshShimmer({ className = '' }) {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();
  if (reduced) return null;
  return (
    <div
      aria-hidden="true"
      data-pm-refresh-shimmer
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      <div
        className="absolute inset-y-0 w-full"
        style={{
          background:
            'linear-gradient(105deg, transparent 40%, rgba(229,25,125,0.06) 50%, transparent 60%)',
          animation: `pmShimmerSweep 1600ms ${MOTION.easeInOut} infinite`,
        }}
      />
    </div>
  );
}

// ── Gallery scaffolding ─────────────────────────────────────

/**
 * `TransitionDemo` — the didactic frame every "Transitions" gallery
 * entry renders through (plan §2.6, Pablo's explicit ask 2026-07-11):
 * live demo area + "Used in:" product locations + one-line mechanism
 * (tokens included) + the export name + a Replay button + a
 * reduced-motion toggle.
 *
 * Replay remounts the demo subtree (key bump); the reduced-motion
 * toggle wraps the demo in `<MotionConfig>` so primitives resolve the
 * override without touching the OS setting. Internal gallery
 * scaffolding — listed in `SHOWCASE_EXCLUDED`, not a product widget.
 */
export function TransitionDemo({ usedIn = [], how, exportName, children }) {
  const [replayKey, setReplayKey] = useState(0);
  const [reduced, setReduced] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div className="relative rounded-lg border border-neutral-100 bg-neutral-50/60 p-4">
        <MotionConfig reducedMotion={reduced ? true : null}>
          <div key={replayKey}>{children}</div>
        </MotionConfig>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setReplayKey((k) => k + 1)}
          className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-600 hover:border-accent/50 hover:text-accent-dark transition-colors"
        >
          {/* replay icon */}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <path d="M3 4v5h5" />
          </svg>
          Replay
        </button>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
          <input
            type="checkbox"
            checked={reduced}
            onChange={(e) => setReduced(e.target.checked)}
            className="h-3 w-3 accent-accent"
          />
          Reduced motion
        </label>
      </div>
      <dl className="space-y-1 text-[11px] leading-relaxed text-neutral-500">
        {usedIn.length > 0 && (
          <div className="flex gap-1.5">
            <dt className="shrink-0 font-semibold text-neutral-600">Used in:</dt>
            <dd>{usedIn.join(' · ')}</dd>
          </div>
        )}
        {how && (
          <div className="flex gap-1.5">
            <dt className="shrink-0 font-semibold text-neutral-600">How:</dt>
            <dd>{how}</dd>
          </div>
        )}
        {exportName && (
          <div className="flex gap-1.5">
            <dt className="shrink-0 font-semibold text-neutral-600">Export:</dt>
            <dd>
              <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-2xs text-neutral-700">
                {exportName}
              </code>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
